/**
 * store.js — Estado centralizado del editor.
 *
 * FASE 3.6: Bridge eliminado. Todos los módulos usan store.get/set directamente.
 *
 * API:
 *   import { store } from '../core/store.js';
 *   store.get('siteData');
 *   store.set('currentPageId', 'home');
 *   store.on('siteBlocks', fn);   // listener reactivo
 */

// La TIPOGRAFÍA es del sitio, no de cada bloque (invariante nº9): su verdad vive en
// `siteData.site_tokens.fontFamily`, con el prop del bloque como fallback retrocompatible. Aquí
// está el único punto por el que pasa TODO cambio de siteData/site_tokens, así que es donde se
// re-resuelve y se deja fijada para el render (utils/typography.js). Sin esto habría que repetir
// la resolución en cada llamante (viewer, creador, Estudio de Tema…) y se desincronizaría.
// typography.js no importa nada → no hay riesgo de ciclo con este módulo.
import { resolverFuenteSitio, fijarFuenteSitio } from '../utils/typography.js';

/**
 * ¿Puede ESTA sesión guardar lo que edite?
 *
 * `ver_sitio` se abre con `view_site`, pero guardar y publicar piden `change_site`. Quien solo
 * tenía «Sitios Web: Lectura» entraba al editor, cambiaba textos y fotos, pulsaba Guardar y
 * recibía un 403: el trabajo a la basura y un aviso que además mentía («sesión expirada»).
 *
 * `web/AIBuilderGenerator/views.py::ver_sitio` calcula el permiso y `ver_sitio.html` lo publica
 * como `window.PUEDE_EDITAR = false`. Ausente ⇒ SÍ se puede (el caso normal, y el creador y el
 * viewer público, que no la emiten) → ningún sitio existente cambia de comportamiento.
 *
 * Bandera PROPIA y no `IS_PUBLIC`: esa significa "esto es el sitio publicado" y arrastra el
 * router público y los hooks del publicado, que aquí no tocan.
 *
 * Se lee en cada llamada (no se congela en un `const`) para que valga igual si alguien la fija
 * más tarde —una prueba manual desde la consola, por ejemplo—.
 */
export function puedeEditar() {
    return typeof window !== 'undefined' ? window.PUEDE_EDITAR !== false : true;
}

/**
 * ¿Arranca la página en MODO EDICIÓN? El sitio publicado y la SPA pública de un borrador
 * (`window.IS_PUBLIC`, la sirve `views.py::_render_builder_public`) NUNCA; quien no puede guardar
 * (`puedeEditar()`), tampoco. Es UNA regla para dos sitios: el estado inicial de aquí abajo y el
 * `store.set('editorMode', …)` que `script.js` (entry del editor) repite al arrancar. Hasta el
 * 01/10/2026 ese segundo sitio no miraba `IS_PUBLIC` y abría la SPA pública de un borrador en
 * modo edición a cualquier visitante anónimo (textos editables, botón «Ver Sitio»).
 */
export function edicionInicial() {
    const w = typeof window !== 'undefined' ? window : {};
    return !w.IS_PUBLIC && puedeEditar();
}

// --- ESTADO INTERNO ---
// Objeto privado — nunca se accede directamente desde fuera del módulo.
const _state = {
    siteData:      null,    // Objeto raíz { site_guidelines, pages[] }
    siteBlocks:    [],      // Bloques de la página activa (referencia mutable)
    currentPageId: 'home',  // ID de la página activa
    currentBlockIndex: null,// Índice del bloque SELECCIONADO (clic) — sustrato de Supr / Ctrl+D / Ctrl+C
    // Editor activado inteligentemente: el sitio publicado NO se edita… y quien no puede
    // guardar, tampoco. `editorMode` es la bandera que consultan 31 módulos del front para
    // apagar varitas, catálogo, arrastre, toolbars y contenteditable: apagándola aquí se apaga
    // todo, sin tocar a sus consumidores.
    // OJO: `script.js` (ENTRY del editor) VUELVE A FIJAR esta clave nada más arrancar; por eso
    // los dos sitios preguntan a la MISMA regla, `edicionInicial()`.
    editorMode:    edicionInicial(),
    dirty:         false,   // Hay cambios sin guardar
    historyAction: false,   // Está ejecutando undo/redo
};

// Mapa de listeners por clave para reactivity futura
const _listeners = new Map();

// --- API PÚBLICA ---
export const store = {

    /** Lee un valor del estado. */
    get(key) {
        return _state[key];
    },

    /**
     * Escribe un valor y notifica a los listeners.
     * No notifica si el valor no cambia (===).
     */
    set(key, value) {
        const old = _state[key];
        if (old === value) return;
        _state[key] = value;
        if (key === 'siteData') fijarFuenteSitio(resolverFuenteSitio(value));
        const fns = _listeners.get(key);
        if (fns) fns.forEach(fn => fn(value, old));
    },

    /**
     * Suscribe un listener a cambios de una clave.
     * Devuelve una función de cancelación (unsubscribe).
     */
    on(key, fn) {
        if (!_listeners.has(key)) _listeners.set(key, new Set());
        _listeners.get(key).add(fn);
        return () => _listeners.get(key).delete(fn);
    },

    /** Devuelve el objeto página activa desde siteData. */
    getCurrentPage() {
        const pages = _state.siteData?.pages || [];
        return pages.find(p => p.id === _state.currentPageId) || pages[0] || null;
    },

    /**
     * Actualiza los bloques de la página activa y marca dirty.
     */
    updateCurrentBlocks(blocks) {
        _state.siteBlocks = blocks;
        this.set('dirty', true);
    },

    /**
     * Fusiona (patch) los tokens de diseño globales del sitio (site_tokens) y marca
     * dirty. Vive a nivel de siteData.site_tokens → persiste en datos_json_builder
     * (limpiar_json_basura NO lo borra). render.js reinyecta site_tokens en cada
     * renderPage → preview en vivo gratis (lo usa el Estudio de Tema).
     *
     * Mutación EN SITIO de site_tokens (objeto pequeño), sin clonar todo siteData:
     * barato para un slider arrastrándose (a diferencia de mutate(), que hace deep
     * clone de todo el árbol de páginas en cada tick). Un valor null/'' BORRA la
     * clave (p.ej. deseleccionar preset) para no ensuciar el JSON.
     * @param {Object} patch - { 'style_preset': 'glass', '--card-radius': '16px', ... }
     */
    setSiteTokens(patch) {
        if (!_state.siteData || !patch) return;
        const tokens = { ...(_state.siteData.site_tokens || {}) };
        Object.entries(patch).forEach(([k, v]) => {
            if (v === null || v === undefined || v === '') delete tokens[k];
            else tokens[k] = v;
        });
        _state.siteData.site_tokens = tokens;
        // `site_tokens` se muta EN SITIO (no pasa por set('siteData')) → la fuente del sitio hay
        // que re-fijarla aquí a mano, o cambiarla en el Estudio de Tema no se vería hasta recargar.
        fijarFuenteSitio(resolverFuenteSitio(_state.siteData));
        this.set('dirty', true);
    },

    /**
     * Clona el valor de `key`, aplica `fn(clone)`, y hace set explícito.
     * Evita mutaciones por referencia al estado interno.
     * @param {string} key - Clave del estado a mutar.
     * @param {function} fn - Función que recibe el clon y lo modifica in-place.
     */
    mutate(key, fn) {
        const clone = JSON.parse(JSON.stringify(_state[key]));
        fn(clone);
        if (key === 'siteData') {
            syncHeaderLinks(clone, _state.currentPageId);
        }
        this.set(key, clone);
    },
};

/**
 * Sincroniza los textos de los enlaces del header entre todas las subpáginas
 * basándose en las coincidencias de URL/dirección de la página activa.
 *
 * Solo los enlaces a PÁGINAS (`?page=…`): su URL ya lleva el idioma (`?page=servicios-en`), así
 * que casar por URL nunca cruza idiomas. Un externo o un ancla (`#contacto`) tiene la MISMA URL en
 * la home y en su espejo `home-en`, y casarlos le ponía al inglés el texto del español en cada
 * `mutate` —y desde que la cabecera es común por idioma (utils/header-sync.js), esos enlaces están
 * en TODAS las páginas—. Sus textos ya viajan, por idioma, con la lista común de la cabecera.
 */
function syncHeaderLinks(siteData, activePageId) {
    if (!siteData || !siteData.pages) return;
    const activePage = siteData.pages.find(p => p.id === activePageId);
    if (!activePage) return;
    const activeHeader = activePage.blocks?.find(b => b.type === 'header');
    if (!activeHeader || !activeHeader.props || !activeHeader.props.links) return;

    // Crear un mapa de url -> text a partir del header de la página activa
    const linkMap = new Map();
    activeHeader.props.links.forEach(link => {
        if (link && typeof link.url === 'string' && link.url.startsWith('?page=')) {
            linkMap.set(link.url, link.text);
        }
    });

    // Sincronizar las demás páginas
    siteData.pages.forEach(page => {
        if (page.id === activePageId) return;
        const header = page.blocks?.find(b => b.type === 'header');
        if (header && header.props && header.props.links) {
            header.props.links.forEach(link => {
                if (link && link.url && linkMap.has(link.url)) {
                    link.text = linkMap.get(link.url);
                }
            });
        }
    });
}

